import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import DeliveryRouteMap from './components/DeliveryRouteMap';
import {
  fetchShippedOrders,
  markOrderShipped,
  updateDeliveryLocation,
  verifyOTPOnly,
  collectPaymentAndDeliver,
  saveNotification,
  generateOTP,
  fetchPaymentRecords,
  saveSizeExchangeDetails,
  saveReturnRequest,
  cancelOrderByDeliveryBoy,
  type Order,
  type PaymentRecord,
  type ReturnItem,
} from '@/lib/supabase';

type PanelView = 'login' | 'suspended' | 'orders' | 'active-delivery' | 'records';
type DeliveryStep = 'map-otp' | 'size-swap' | 'payment' | 'done';

interface SizeSwapEntry {
  itemIndex: number;
  orderedSize: string;
  deliveredSize: string; // empty string means no swap (same as ordered)
}

const DB_ID_KEY = 'delivery_boy_id';
const DB_NAME_KEY = 'delivery_boy_name';
const DB_PHONE_KEY = 'delivery_boy_phone';
const QR_STORAGE_KEY = 'company_payment_qr';

interface DeliveryBoyRow {
  id: string;
  name: string;
  phone: string;
  password: string;
  status: 'active' | 'inactive';
  area: string | null;
}

export default function DeliveryPanel() {
  const [view, setView] = useState<PanelView>('login');
  const [dbId, setDbId] = useState('');
  const [dbName, setDbName] = useState('');
  const [dbPhone, setDbPhone] = useState('');
  const [dbStatus, setDbStatus] = useState<'active' | 'inactive'>('active');

  // Login form
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [deliveryStep, setDeliveryStep] = useState<DeliveryStep>('map-otp');

  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [selectedPayment, setSelectedPayment] = useState<'cash' | 'online' | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Size swap state
  const [sizeSwaps, setSizeSwaps] = useState<SizeSwapEntry[]>([]);

  // Return state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnOrderId, setReturnOrderId] = useState('');
  const [returnOrderForModal, setReturnOrderForModal] = useState<Order | null>(null);
  const [returnItems, setReturnItems] = useState<{ itemIndex: number; selected: boolean; reason: string }[]>([]);
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const [locationStatus, setLocationStatus] = useState<'idle' | 'tracking' | 'error'>('idle');
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const setCurrentLocationWithRef = (loc: { lat: number; lng: number } | null) => {
    currentLocationRef.current = loc;
    setCurrentLocation(loc);
  };
  const locationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const currentLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [recordPeriod, setRecordPeriod] = useState<'today' | 'month' | 'all'>('today');
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [companyQr, setCompanyQr] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Auto-login from saved session
  useEffect(() => {
    const savedId = localStorage.getItem(DB_ID_KEY);
    const savedName = localStorage.getItem(DB_NAME_KEY);
    const savedPhone = localStorage.getItem(DB_PHONE_KEY);
    if (savedId && savedName && savedPhone) {
      // Re-verify status from DB
      supabase.from('delivery_boys').select('status').eq('id', savedId).maybeSingle().then(({ data }) => {
        if (data) {
          setDbId(savedId);
          setDbName(savedName);
          setDbPhone(savedPhone);
          setDbStatus(data.status);
          setView(data.status === 'active' ? 'orders' : 'suspended');
        } else {
          // Account deleted, clear session
          localStorage.removeItem(DB_ID_KEY);
          localStorage.removeItem(DB_NAME_KEY);
          localStorage.removeItem(DB_PHONE_KEY);
        }
      });
    }
    const savedQr = localStorage.getItem(QR_STORAGE_KEY);
    if (savedQr) setCompanyQr(savedQr);
  }, []);

  // ── LOGIN HANDLER ──────────────────────────────────────────────────────────
  const handleLogin = async () => {
    if (loginPhone.trim().length < 10) {
      setLoginError('10 digit phone number daalo');
      return;
    }
    if (!loginPassword.trim()) {
      setLoginError('Password daalo');
      return;
    }
    setLoginLoading(true);
    setLoginError('');

    const { data, error } = await supabase
      .from('delivery_boys')
      .select('id, name, phone, password, status, area')
      .eq('phone', loginPhone.trim())
      .maybeSingle();

    setLoginLoading(false);

    if (error || !data) {
      setLoginError('Yeh phone number registered nahi hai');
      return;
    }

    const boy = data as DeliveryBoyRow;
    if (boy.password !== loginPassword.trim()) {
      setLoginError('Password galat hai');
      return;
    }

    // Save session
    localStorage.setItem(DB_ID_KEY, boy.id);
    localStorage.setItem(DB_NAME_KEY, boy.name);
    localStorage.setItem(DB_PHONE_KEY, boy.phone);

    setDbId(boy.id);
    setDbName(boy.name);
    setDbPhone(boy.phone);
    setDbStatus(boy.status);

    if (boy.status === 'inactive') {
      setView('suspended');
    } else {
      setView('orders');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(DB_ID_KEY);
    localStorage.removeItem(DB_NAME_KEY);
    localStorage.removeItem(DB_PHONE_KEY);
    stopLocationTracking();
    setView('login');
    setLoginPhone('');
    setLoginPassword('');
    setActiveOrder(null);
    setOrders([]);
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    const data = await fetchShippedOrders();
    setOrders(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (view === 'orders') loadOrders();
  }, [view, loadOrders]);

  useEffect(() => {
    if (view !== 'orders') return;
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
  }, [view, loadOrders]);

  const loadRecords = useCallback(async () => {
    if (!dbPhone) return;
    setRecordsLoading(true);
    const data = await fetchPaymentRecords(dbPhone, recordPeriod);
    setRecords(data);
    setRecordsLoading(false);
  }, [dbPhone, recordPeriod]);

  useEffect(() => {
    if (view === 'records') loadRecords();
  }, [view, recordPeriod, loadRecords]);

  const startLocationTracking = useCallback((orderId: string) => {
    if (!navigator.geolocation) {
      setLocationStatus('error');
      showToast('GPS not supported on this device', 'error');
      return;
    }

    // Clear any existing interval first
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }

    setLocationStatus('tracking');

    const updateLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setCurrentLocationWithRef({ lat: latitude, lng: longitude });
          setLocationStatus('tracking');
          updateDeliveryLocation(orderId, latitude, longitude).catch(() => {});
        },
        (err) => {
          // Use ref to avoid stale closure issue
          const hasLocation = currentLocationRef.current !== null;
          if (!hasLocation) {
            // Only show error if we never got a location
            setLocationStatus('error');
            // Better error messages based on error code
            let errorMsg = 'Location unavailable';
            if (err.code === 1) {
              errorMsg = 'GPS permission denied. Phone settings mein Location ON karo!';
            } else if (err.code === 2) {
              errorMsg = 'GPS signal nahi mil raha. Open area mein jao!';
            } else if (err.code === 3) {
              errorMsg = 'GPS timeout. Dobara try karo!';
            } else if (err.message) {
              errorMsg = err.message;
            }
            showToast(`GPS Error: ${errorMsg}`, 'error');
          }
          // If we already have a location, silently retry — don't break tracking
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 }
      );
    };

    // Immediate first update
    updateLocation();

    // Set up interval for continuous updates
    locationIntervalRef.current = setInterval(updateLocation, 10000); // Update every 10 seconds
  }, [showToast]);

  const stopLocationTracking = useCallback(() => {
    if (locationIntervalRef.current) { clearInterval(locationIntervalRef.current); locationIntervalRef.current = null; }
    if (watchIdRef.current !== null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    currentLocationRef.current = null;
    setLocationStatus('idle');
  }, []);

  useEffect(() => { return () => stopLocationTracking(); }, [stopLocationTracking]);

  // Check if direct delivery is enabled (bina OTP ke) - real-time check
  const isDirectDeliveryEnabled = () => {
    return localStorage.getItem('direct_delivery_enabled') === 'true';
  };

  const handlePickupOrder = useCallback(async (order: Order) => {
    // Real-time check from localStorage - no caching
    const directDelivery = localStorage.getItem('direct_delivery_enabled') === 'true';
    const otp = directDelivery ? '000000' : generateOTP(); // Dummy OTP for direct delivery
    const result = await markOrderShipped(order.id, dbName, dbPhone, otp);
    if (!result.success) { showToast('Order pick karne mein error aaya', 'error'); return; }
    await saveNotification(order, 'order_shipped');
    const updatedOrder = { ...order, status: 'shipped' as const, delivery_otp: otp, delivery_boy_name: dbName, delivery_boy_phone: dbPhone };
    setActiveOrder(updatedOrder);
    
    // If direct delivery enabled, skip OTP step and go directly to size-swap
    if (directDelivery) {
      setOtpVerified(true);
      setOtpInput('000000');
      // Initialize size swaps
      const swapEntries: SizeSwapEntry[] = updatedOrder.items.map((item, idx) => ({
        itemIndex: idx,
        orderedSize: item.size ?? '',
        deliveredSize: item.size ?? '',
      }));
      setSizeSwaps(swapEntries);
      setDeliveryStep('size-swap');
      showToast('Direct Delivery Mode — OTP skip!', 'success');
    } else {
      setDeliveryStep('map-otp');
      setOtpInput(''); setOtpError(''); setOtpVerified(false);
    }
    
    setSelectedPayment(null);
    setView('active-delivery');
    // Small delay to ensure state is updated before starting GPS
    setTimeout(() => startLocationTracking(order.id), 200);
    showToast('Order pick kar liya!', 'success');
  }, [dbName, dbPhone, showToast, startLocationTracking]);

  const handleVerifyOTP = useCallback(async () => {
    if (!activeOrder) return;
    if (otpInput.length !== 6) { setOtpError('6 digit OTP daalo'); return; }
    setOtpLoading(true); setOtpError('');
    const result = await verifyOTPOnly(activeOrder.id, otpInput);
    setOtpLoading(false);
    if (!result.success) { setOtpError(result.error || 'Galat OTP hai'); return; }
    setOtpVerified(true);
    stopLocationTracking();
    showToast('OTP sahi hai!', 'success');
    // Initialize size swaps — one entry per item that has a size
    const swapEntries: SizeSwapEntry[] = activeOrder.items.map((item, idx) => ({
      itemIndex: idx,
      orderedSize: item.size ?? '',
      deliveredSize: item.size ?? '', // default: same as ordered
    }));
    setSizeSwaps(swapEntries);
    setTimeout(() => setDeliveryStep('size-swap'), 600);
  }, [activeOrder, otpInput, stopLocationTracking, showToast]);

  // Apply size swap stock logic and proceed to payment
  const handleConfirmSizeSwap = useCallback(() => {
    if (!activeOrder) return;
    try {
      const stored = localStorage.getItem('listed_products');
      if (stored) {
        const listed = JSON.parse(stored);
        if (Array.isArray(listed)) {
          const updated = listed.map((p: {
            id: number; name?: string; price?: number; stock?: number;
            outOfStock?: boolean; stockBreakdown?: Record<string, unknown>;
            colors?: { name: string }[]; sizes?: string[];
          }) => {
            // Find if any swap affects this product
            const swappedItems = sizeSwaps.filter(sw => {
              const item = activeOrder.items[sw.itemIndex];
              return (item.id === p.id || (item.name === p.name && item.price === p.price))
                && sw.deliveredSize !== sw.orderedSize
                && sw.orderedSize !== ''
                && sw.deliveredSize !== '';
            });
            if (swappedItems.length === 0) return p;

            let newBreakdown = { ...(p.stockBreakdown ?? {}) };
            const hasColors = (p.colors?.length ?? 0) > 0;
            const hasSizes = (p.sizes?.length ?? 0) > 0;

            swappedItems.forEach(sw => {
              const item = activeOrder.items[sw.itemIndex];
              const colorName = item.selectedColor?.name;
              const qty = item.quantity;

              if (hasColors && hasSizes && colorName) {
                // Return ordered size (stock +)
                const ordColorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
                const prevOrd = typeof ordColorMap[sw.orderedSize] === 'number' ? ordColorMap[sw.orderedSize] : 0;
                // Deduct delivered size (stock -)
                const prevDel = typeof ordColorMap[sw.deliveredSize] === 'number' ? ordColorMap[sw.deliveredSize] : 0;
                newBreakdown[colorName] = {
                  ...ordColorMap,
                  [sw.orderedSize]: prevOrd + qty,
                  [sw.deliveredSize]: Math.max(0, prevDel - qty),
                };
              } else if (!hasColors && hasSizes) {
                // Return ordered size
                const prevOrd = typeof newBreakdown[sw.orderedSize] === 'number' ? (newBreakdown[sw.orderedSize] as number) : 0;
                newBreakdown[sw.orderedSize] = prevOrd + qty;
                // Deduct delivered size
                const prevDel = typeof newBreakdown[sw.deliveredSize] === 'number' ? (newBreakdown[sw.deliveredSize] as number) : 0;
                newBreakdown[sw.deliveredSize] = Math.max(0, prevDel - qty);
              }
            });

            // Recalculate total
            let newTotal = 0;
            Object.values(newBreakdown).forEach(v => {
              if (typeof v === 'number') newTotal += v;
              else if (typeof v === 'object' && v !== null) {
                Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
              }
            });

            return { ...p, stock: newTotal, outOfStock: newTotal === 0, stockBreakdown: newBreakdown };
          });
          localStorage.setItem('listed_products', JSON.stringify(updated));
        }
      }
    } catch (e) {
      console.error('Size swap stock update failed:', e);
    }
    setDeliveryStep('payment');
  }, [activeOrder, sizeSwaps]);

  const handleConfirmPayment = useCallback(async () => {
    if (!activeOrder || !selectedPayment) return;
    setPaymentLoading(true); setPaymentError('');
    const result = await collectPaymentAndDeliver(activeOrder.id, selectedPayment);
    setPaymentLoading(false);
    if (!result.success) { setPaymentError(result.error || 'Payment save karne mein error aaya'); return; }
    // Save size exchange details if any swap happened
    const swapDetails = sizeSwaps
      .filter(s => s.orderedSize !== '')
      .map(s => ({
        itemIndex: s.itemIndex,
        itemName: activeOrder.items[s.itemIndex]?.name || '',
        orderedSize: s.orderedSize,
        deliveredSize: s.deliveredSize,
      }));
    if (swapDetails.length > 0) {
      await saveSizeExchangeDetails(activeOrder.id, swapDetails);
    }
    await saveNotification(activeOrder, 'order_delivered');
    setDeliveryStep('done');
    showToast('Delivery complete!', 'success');
  }, [activeOrder, selectedPayment, sizeSwaps, showToast]);

  const handleBackToOrders = () => {
    stopLocationTracking();
    setActiveOrder(null); setOtpInput(''); setOtpError(''); setOtpVerified(false);
    setSelectedPayment(null); setDeliveryStep('map-otp'); setSizeSwaps([]);
    setView('orders'); loadOrders();
  };

  const openReturnModal = useCallback((order: Order) => {
    setReturnOrderId(order.id);
    setReturnOrderForModal(order);
    setReturnItems(order.items.map((_, idx) => ({ itemIndex: idx, selected: false, reason: '' })));
    setShowReturnModal(true);
  }, []);

  const RETURN_REASONS = [
    'Customer ne lene se mana kar diya',
    'Customer ghar pe nahi tha',
    'Customer ne cancel kar diya',
    'Size nahi hua',
    'Product damaged tha',
    'Wrong product deliver hua',
  ];

  // Shared stock restore helper — used on both full-order reject and partial return
  const restoreStockForItems = useCallback((
    orderRef: Order,
    itemIndices: { itemIndex: number }[],
    // Optional: if size-swap happened, orderedSize is the ORIGINAL size that was deducted
    swapOverrides?: Record<number, string> // itemIndex → original ordered size
  ) => {
    try {
      const stored = localStorage.getItem('listed_products');
      if (!stored) return;
      const listed = JSON.parse(stored);
      if (!Array.isArray(listed)) return;

      const updated = listed.map((p: {
        id: number; name?: string; price?: number; stock?: number;
        outOfStock?: boolean; stockBreakdown?: Record<string, unknown>;
        colors?: { name: string }[]; sizes?: string[];
      }) => {
        // Find all selected items that match this product
        const matches = itemIndices.filter(({ itemIndex }) => {
          const item = orderRef.items[itemIndex];
          return item && (item.id === p.id || (item.name === p.name && item.price === p.price));
        });
        if (matches.length === 0) return p;

        let newBreakdown = { ...(p.stockBreakdown ?? {}) };
        const hasSizes = (p.sizes?.length ?? 0) > 0;
        const hasColors = (p.colors?.length ?? 0) > 0;

        matches.forEach(({ itemIndex }) => {
          const item = orderRef.items[itemIndex];
          const colorName = item.selectedColor?.name;
          // Use swap override if provided (i.e. return after size-swap = restore DELIVERED size)
          const sizeName = swapOverrides?.[itemIndex] ?? item.size;
          const qty = item.quantity;

          if (hasColors && hasSizes && colorName && sizeName) {
            const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
            const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
            newBreakdown[colorName] = { ...colorMap, [sizeName]: prev + qty };
          } else if (!hasColors && hasSizes && sizeName) {
            const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
            newBreakdown[sizeName] = prev + qty;
          } else if (hasColors && !hasSizes && colorName) {
            const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
            newBreakdown[colorName] = prev + qty;
          } else {
            const prev = typeof newBreakdown['total'] === 'number'
              ? (newBreakdown['total'] as number)
              : (p.stock ?? 0);
            newBreakdown['total'] = prev + qty;
          }
        });

        // Recalculate total stock
        let newTotal = 0;
        Object.values(newBreakdown).forEach(v => {
          if (typeof v === 'number') newTotal += v;
          else if (typeof v === 'object' && v !== null) {
            Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
          }
        });

        return { ...p, stock: newTotal, outOfStock: newTotal === 0, stockBreakdown: newBreakdown };
      });

      localStorage.setItem('listed_products', JSON.stringify(updated));
    } catch (e) {
      console.error('Stock restore failed:', e);
    }
  }, []);

  const handleSubmitReturn = useCallback(async () => {
    if (!returnOrderForModal) return;
    const selectedItems = returnItems.filter(i => i.selected);
    if (selectedItems.length === 0) { showToast('Koi item select nahi kiya', 'error'); return; }
    const noReason = selectedItems.find(i => !i.reason);
    if (noReason) { showToast('Har item ke liye reason daalo', 'error'); return; }

    setReturnSubmitting(true);

    const returnPayload: ReturnItem[] = selectedItems.map(ri => ({
      itemName: returnOrderForModal.items[ri.itemIndex]?.name || '',
      itemImage: returnOrderForModal.items[ri.itemIndex]?.image || '',
      quantity: returnOrderForModal.items[ri.itemIndex]?.quantity || 1,
      reason: ri.reason,
    }));

    // ── Stock Restore ───────────────────────────────────────────────────────
    // Determine if size-swap happened for any of the selected items
    // If swap happened → we deducted delivered size, so restore delivered size
    // If no swap → restore original ordered size (item.size)
    const swapOverrides: Record<number, string> = {};
    if (sizeSwaps.length > 0) {
      sizeSwaps.forEach(sw => {
        const isSelected = selectedItems.some(ri => ri.itemIndex === sw.itemIndex);
        if (isSelected && sw.deliveredSize && sw.deliveredSize !== sw.orderedSize) {
          // Stock was deducted for deliveredSize, so restore that
          swapOverrides[sw.itemIndex] = sw.deliveredSize;
        }
      });
    }
    restoreStockForItems(returnOrderForModal, selectedItems, swapOverrides);
    // ───────────────────────────────────────────────────────────────────────

    const mainReason = selectedItems
      .map(ri => `${returnOrderForModal.items[ri.itemIndex]?.name}: ${ri.reason}`)
      .join('; ');

    // Cancel the order + save return details in one go
    const result = await cancelOrderByDeliveryBoy(returnOrderId, returnPayload, mainReason);
    setReturnSubmitting(false);

    if (result.success) {
      // Send cancelled notification to customer
      await saveNotification(returnOrderForModal, 'order_cancelled', mainReason);
      showToast('Order reject ho gaya, stock wapas aa gaya!', 'success');
      setShowReturnModal(false);
      setReturnOrderId('');
      setReturnOrderForModal(null);
      setReturnItems([]);
      handleBackToOrders();
    } else {
      showToast('Error aaya, dobara try karo', 'error');
    }
  }, [returnOrderForModal, returnOrderId, returnItems, sizeSwaps, restoreStockForItems, showToast, handleBackToOrders]);

  // Map always shows CUSTOMER location (destination) - delivery boy needs to see where to go!
  // Delivery boy's current location is shown separately with distance indicator
  const mapUrl = activeOrder?.customer_lat && activeOrder?.customer_lng
    ? `https://maps.google.com/maps?q=${activeOrder.customer_lat},${activeOrder.customer_lng}&z=16&output=embed`
    : `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110204.5877174017!2d77.934733!3d30.325565!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390929c356c888af%3A0x4c3562c03251865!2sDehradun%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin`;

  // Haversine formula — calculate distance between two lat/lng points in km
  const calcDistance = (
    lat1: number, lng1: number,
    lat2: number, lng2: number
  ): number => {
    const R = 6371; // Earth radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const distanceToCustomer: number | null =
    currentLocation && activeOrder?.customer_lat && activeOrder?.customer_lng
      ? calcDistance(
          currentLocation.lat,
          currentLocation.lng,
          activeOrder.customer_lat,
          activeOrder.customer_lng
        )
      : null;

  const distanceLabel =
    distanceToCustomer === null
      ? null
      : distanceToCustomer < 1
      ? `${Math.round(distanceToCustomer * 1000)} m`
      : `${distanceToCustomer.toFixed(1)} km`;

  const distanceColor =
    distanceToCustomer === null
      ? locationStatus === 'error'
        ? 'bg-red-50 text-red-700 border-red-300'
        : 'bg-gray-100 text-gray-500 border-gray-200'
      : distanceToCustomer <= 0.5
      ? 'bg-green-50 text-green-700 border-green-300'
      : distanceToCustomer <= 2
      ? 'bg-amber-50 text-amber-700 border-amber-300'
      : 'bg-gray-50 text-gray-600 border-gray-200';

  const distanceIcon =
    distanceToCustomer === null
      ? 'ri-map-pin-time-line'
      : distanceToCustomer <= 0.5
      ? 'ri-checkbox-circle-line'
      : distanceToCustomer <= 2
      ? 'ri-walk-line'
      : 'ri-e-bike-2-line';

  const distanceStatus =
    distanceToCustomer === null
      ? locationStatus === 'error'
        ? 'GPS error — phone settings check karo'
        : 'GPS se location lo'
      : distanceToCustomer <= 0.5
      ? 'Almost there!'
      : distanceToCustomer <= 2
      ? 'Getting close...'
      : 'On the way';

  const cashTotal = records.filter(r => r.collected_payment_method === 'cash').reduce((s, r) => s + r.final_amount, 0);
  const onlineTotal = records.filter(r => r.collected_payment_method === 'online').reduce((s, r) => s + r.final_amount, 0);
  const cashCount = records.filter(r => r.collected_payment_method === 'cash').length;
  const onlineCount = records.filter(r => r.collected_payment_method === 'online').length;

  // ── LOGIN VIEW ──────────────────────────────────────────────────────────────
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center mx-auto mb-4">
              <i className="ri-e-bike-2-fill text-white text-4xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Delivery Panel</h1>
            <p className="text-gray-500 text-sm mt-1">ClothyBazar Delivery Boy Login</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="space-y-4">
              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="tel"
                    value={loginPhone}
                    onChange={e => { setLoginPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setLoginError(''); }}
                    placeholder="10 digit phone number"
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type={showLoginPw ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={e => { setLoginPassword(e.target.value); setLoginError(''); }}
                    placeholder="Password daalo"
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    className="w-full pl-9 pr-10 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPw(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 cursor-pointer"
                  >
                    <i className={showLoginPw ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <i className="ri-error-warning-line text-red-500 shrink-0"></i>
                  <p className="text-red-600 text-xs font-medium">{loginError}</p>
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={loginLoading}
                className="w-full py-3.5 rounded-xl bg-gray-900 text-white font-bold text-sm hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loginLoading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Login ho raha hai...</>
                ) : (
                  <><i className="ri-login-box-line"></i>Login Karo</>
                )}
              </button>
            </div>

            <div className="mt-4 bg-gray-50 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-information-line text-gray-400 text-sm shrink-0 mt-0.5"></i>
              <p className="text-gray-500 text-xs leading-relaxed">
                Admin ne jo phone number aur password diya hai, wahi yahan daalo
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── SUSPENDED VIEW ──────────────────────────────────────────────────────────
  if (view === 'suspended') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <i className="ri-forbid-2-line text-red-500 text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Suspended</h2>
          <p className="text-gray-500 text-sm mb-2">
            Aapka account ({dbName}) temporarily deactivate kar diya gaya hai
          </p>
          <p className="text-gray-400 text-xs mb-8">
            Admin se contact karo account activate karwane ke liye
          </p>

          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                <i className="ri-user-forbid-line text-red-500"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">{dbName}</p>
                <p className="text-gray-500 text-xs">{dbPhone}</p>
              </div>
              <span className="ml-auto px-2.5 py-1 rounded-full bg-red-200 text-red-700 text-xs font-bold">
                Suspended
              </span>
            </div>
            <p className="text-red-600 text-xs flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>
              Yeh account abhi kaam nahi kar sakta. Admin se baat karo.
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="w-full py-3.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 hover:bg-gray-100 transition-all"
          >
            <i className="ri-logout-box-line"></i>
            Alag Account Se Login Karo
          </button>
        </div>
      </div>
    );
  }

  // ── ACTIVE DELIVERY VIEW ────────────────────────────────────────────────────
  if (view === 'active-delivery' && activeOrder) {
    return (
      <div className="min-h-screen bg-gray-50">
        {toast && (
          <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
            toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
          }`}>
            <i className={toast.type === 'success' ? 'ri-check-line' : toast.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
            {toast.msg}
          </div>
        )}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 h-14 flex items-center justify-between">
            <button onClick={handleBackToOrders} className="flex items-center gap-2 text-gray-600 text-sm font-medium cursor-pointer">
              <i className="ri-arrow-left-line text-lg"></i>Orders
            </button>
            
            {/* Direct Delivery Badge */}
            {isDirectDeliveryEnabled() && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                <i className="ri-shield-check-line"></i>Direct Mode
              </span>
            )}
            
            {deliveryStep === 'map-otp' && !isDirectDeliveryEnabled() && (
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                locationStatus === 'tracking' ? 'bg-green-100 text-green-700' :
                locationStatus === 'error' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <span className={`w-2 h-2 rounded-full ${locationStatus === 'tracking' ? 'bg-green-500 animate-pulse' : locationStatus === 'error' ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                {locationStatus === 'tracking' ? 'Live Location ON' : locationStatus === 'error' ? 'GPS Error' : 'Location Off'}
              </span>
            )}
            {deliveryStep === 'payment' && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                <i className="ri-money-rupee-circle-line"></i>Payment Step
              </span>
            )}
          </div>
        </header>

        {deliveryStep !== 'done' && (
          <div className="bg-white border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-1">
              {/* Step 1: OTP (hidden in direct delivery mode) */}
              {!isDirectDeliveryEnabled() && (
                <>
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${deliveryStep === 'map-otp' ? 'text-gray-900' : 'text-green-600'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${deliveryStep === 'map-otp' ? 'bg-gray-900 text-white' : 'bg-green-500 text-white'}`}>
                      {deliveryStep === 'map-otp' ? '1' : <i className="ri-check-line text-xs"></i>}
                    </div>
                    <span className="whitespace-nowrap">OTP</span>
                  </div>
                  <div className={`flex-1 h-0.5 ${otpVerified ? 'bg-green-400' : 'bg-gray-200'}`}></div>
                </>
              )}
              
              {/* Direct Delivery Badge in step bar */}
              {isDirectDeliveryEnabled() && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 mr-2">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-purple-500 text-white">
                    <i className="ri-shield-check-line text-xs"></i>
                  </div>
                  <span className="whitespace-nowrap">Direct</span>
                </div>
              )}
              
              {/* Step 2: Size Check */}
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${
                deliveryStep === 'size-swap' ? 'text-gray-900' :
                deliveryStep === 'payment' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  deliveryStep === 'size-swap' ? 'bg-gray-900 text-white' :
                  deliveryStep === 'payment' ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-400'
                }`}>
                  {isDirectDeliveryEnabled() ? (deliveryStep === 'size-swap' ? '1' : deliveryStep === 'payment' ? <i className="ri-check-line text-xs"></i> : '2') : (deliveryStep === 'payment' ? <i className="ri-check-line text-xs"></i> : '2')}
                </div>
                <span className="whitespace-nowrap">Size</span>
              </div>
              <div className={`flex-1 h-0.5 ${deliveryStep === 'payment' ? 'bg-green-400' : 'bg-gray-200'}`}></div>
              {/* Step 3: Payment */}
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${deliveryStep === 'payment' ? 'text-gray-900' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${deliveryStep === 'payment' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {isDirectDeliveryEnabled() ? (deliveryStep === 'payment' ? '2' : '2') : '3'}
                </div>
                <span className="whitespace-nowrap">Payment</span>
              </div>
            </div>
          </div>
        )}

        {deliveryStep === 'done' && (
          <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center">
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-6 animate-bounce">
              <i className="ri-checkbox-circle-fill text-green-500 text-5xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Delivery Complete!</h2>
            <p className="text-gray-500 mb-2">Order #{activeOrder.id.toUpperCase().slice(0, 8)}</p>
            <p className="text-gray-500 text-sm mb-4"><i className="ri-user-line mr-1"></i>{activeOrder.customer_name} ko deliver ho gaya</p>

            {/* Size swap summary if any swaps happened */}
            {sizeSwaps.some(s => s.deliveredSize !== s.orderedSize && s.orderedSize !== '') && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 w-full max-w-xs mb-4 text-left">
                <p className="text-amber-800 font-bold text-xs mb-2 flex items-center gap-1.5">
                  <i className="ri-swap-line"></i>Size Swaps Done
                </p>
                {sizeSwaps.filter(s => s.deliveredSize !== s.orderedSize && s.orderedSize !== '').map(sw => (
                  <div key={sw.itemIndex} className="flex items-center gap-2 text-xs text-amber-700">
                    <span className="font-medium truncate max-w-[100px]">{activeOrder.items[sw.itemIndex]?.name}</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-200 font-bold">{sw.orderedSize}</span>
                    <i className="ri-arrow-right-line text-amber-500"></i>
                    <span className="px-1.5 py-0.5 rounded bg-amber-400 text-white font-bold">{sw.deliveredSize}</span>
                  </div>
                ))}
              </div>
            )}

            <div className={`border rounded-2xl p-5 w-full max-w-xs mb-8 ${selectedPayment === 'cash' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <i className={`text-2xl ${selectedPayment === 'cash' ? 'ri-money-rupee-circle-fill text-amber-600' : 'ri-qr-code-line text-green-600'}`}></i>
                <p className={`font-bold text-lg ${selectedPayment === 'cash' ? 'text-amber-700' : 'text-green-700'}`}>₹{activeOrder.final_amount}</p>
              </div>
              <p className={`text-sm font-semibold ${selectedPayment === 'cash' ? 'text-amber-600' : 'text-green-600'}`}>
                {selectedPayment === 'cash' ? 'Cash Collect Kiya' : 'Online Payment Hua'}
              </p>
            </div>
            <button onClick={handleBackToOrders} className="w-full max-w-xs py-4 rounded-2xl bg-gray-900 text-white font-bold text-base cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-2"></i>Wapas Orders Pe
            </button>
          </div>
        )}

        {deliveryStep === 'map-otp' && (
          <div className="p-4 space-y-4 pb-8">
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-user-3-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">{activeOrder.customer_name}</p>
                  <a href={`tel:${activeOrder.customer_phone}`} className="text-gray-500 text-xs flex items-center gap-1 cursor-pointer">
                    <i className="ri-phone-line text-green-500"></i>{activeOrder.customer_phone}
                    <span className="text-green-600 font-medium ml-1">Call</span>
                  </a>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-gray-900 font-bold text-lg">₹{activeOrder.final_amount}</p>
                  <p className="text-gray-400 text-xs">Total</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-map-pin-2-fill text-red-500 mt-0.5 shrink-0"></i>
                <div>
                  <p className="text-gray-700 text-sm font-medium">{activeOrder.customer_address}</p>
                  <p className="text-gray-500 text-xs">{activeOrder.customer_city} {activeOrder.customer_pincode ? `- ${activeOrder.customer_pincode}` : ''}</p>
                </div>
              </div>
              {activeOrder.customer_lat && activeOrder.customer_lng && (
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${activeOrder.customer_lat},${activeOrder.customer_lng}`} target="_blank" rel="noopener noreferrer"
                  className="mt-3 w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  <i className="ri-navigation-fill"></i>Google Maps Mein Navigate Karo
                </a>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">Order Items</p>
              <div className="space-y-2">
                {activeOrder.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <img src={item.image} alt={item.name} className="w-10 h-12 object-cover object-top rounded-lg shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-sm font-medium truncate">{item.name}</p>
                      {item.size && <p className="text-gray-400 text-xs">Size: {item.size}</p>}
                    </div>
                    <p className="text-gray-700 text-sm font-bold shrink-0">×{item.quantity}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Distance Banner */}
            <div className={`rounded-2xl border-2 p-4 flex items-center gap-4 ${distanceColor}`}>
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                distanceToCustomer === null 
                  ? locationStatus === 'error' ? 'bg-red-100' : 'bg-gray-200'
                  : distanceToCustomer <= 0.5 ? 'bg-green-100'
                  : distanceToCustomer <= 2 ? 'bg-amber-100' : 'bg-gray-100'
              }`}>
                <i className={`${distanceIcon} text-2xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-0.5">Customer se doori</p>
                <p className="text-2xl font-bold leading-none">
                  {distanceLabel ?? '—'}
                </p>
                <p className="text-xs font-medium mt-1 opacity-80">{distanceStatus}</p>
                {locationStatus === 'error' && (
                  <p className="text-[10px] mt-1 opacity-70">
                    Phone Settings → Location → ON karo
                  </p>
                )}
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-2">
                {locationStatus === 'tracking' ? (
                  <span className="flex items-center gap-1 text-xs font-bold">
                    <span className={`w-2 h-2 rounded-full animate-pulse ${distanceToCustomer !== null && distanceToCustomer <= 0.5 ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                    Live
                  </span>
                ) : locationStatus === 'error' ? (
                  <>
                    <button
                      onClick={() => { setLocationStatus('idle'); setTimeout(() => startLocationTracking(activeOrder.id), 100); }}
                      className="text-xs font-bold cursor-pointer flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded-lg"
                    >
                      <i className="ri-refresh-line"></i>Retry GPS
                    </button>
                    <button
                      onClick={() => {
                        // Try to open location settings (works on some mobile browsers)
                        if (typeof window !== 'undefined' && window.location) {
                          // For Android - open location settings
                          const isAndroid = /Android/i.test(navigator.userAgent);
                          if (isAndroid) {
                            window.open('intent:#Intent;action=android.settings.LOCATION_SOURCE_SETTINGS;end', '_blank');
                          } else {
                            // iOS or others - show instructions
                            showToast('Phone Settings → Privacy → Location Services → ON', 'info');
                          }
                        }
                      }}
                      className="text-[10px] font-medium cursor-pointer flex items-center gap-1 text-red-500 underline"
                    >
                      <i className="ri-settings-3-line"></i>Settings
                    </button>
                  </>
                ) : (
                  <span className="text-xs opacity-50">GPS off</span>
                )}
              </div>
            </div>

            {/* Live Animated Delivery Map — ClothyBazar in-app tracking */}
            <DeliveryRouteMap
              currentLocation={currentLocation}
              customerLat={activeOrder.customer_lat ?? null}
              customerLng={activeOrder.customer_lng ?? null}
              customerAddress={`${activeOrder.customer_address}, ${activeOrder.customer_city}`}
              locationStatus={locationStatus}
              dbName={dbName}
              activeOrderId={activeOrder.id}
              startLocationTracking={startLocationTracking}
              showToast={showToast}
            />



            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-shield-keyhole-fill text-amber-600"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Delivery OTP Verify Karo</p>
                  <p className="text-gray-500 text-xs">Customer se 6 digit OTP lo aur yahan daalo</p>
                </div>
              </div>
              <input
                type="number"
                value={otpInput}
                onChange={e => { setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                placeholder="6 digit OTP"
                className="w-full px-4 py-4 rounded-xl border-2 border-gray-200 text-center text-2xl font-mono font-bold tracking-[0.3em] focus:border-amber-400 outline-none mb-3"
                maxLength={6}
              />
              {otpError && <p className="text-red-500 text-xs mb-3 flex items-center gap-1"><i className="ri-error-warning-line"></i>{otpError}</p>}
              <button onClick={handleVerifyOTP} disabled={otpLoading || otpInput.length !== 6}
                className="w-full py-4 rounded-xl bg-amber-500 text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {otpLoading ? <><i className="ri-loader-4-line animate-spin"></i>Verify ho raha hai...</> : <><i className="ri-shield-check-line text-xl"></i>OTP Verify Karo</>}
              </button>

              {/* Return Button - Customer ne order nahi liya */}
              <button 
                onClick={() => {
                  if (activeOrder) {
                    openReturnModal(activeOrder);
                  } else {
                    showToast('Order load nahi hua, refresh karo', 'error');
                  }
                }}
                className="w-full py-3 rounded-xl bg-red-50 border-2 border-red-200 text-red-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap mt-3"
              >
                <i className="ri-arrow-go-back-line text-base"></i>Customer Ne Lene Se Mana Kiya - Return Karo
              </button>
            </div>
          </div>
        )}

        {deliveryStep === 'size-swap' && (
          <div className="p-4 space-y-4 pb-8">
            {/* Info banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-t-shirt-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="text-amber-800 font-bold text-sm">Size Check Karo</p>
                <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                  Agar customer ka ordered size fit nahi hua, toh naya size select karo. Ordered size ka stock wapas aa jaayega aur delivered size ka stock minus hoga.
                </p>
              </div>
            </div>

            {/* Return Button - Customer ne order nahi liya (in size-swap step too) */}
            <button 
              onClick={() => {
                if (activeOrder) {
                  openReturnModal(activeOrder);
                } else {
                  showToast('Order load nahi hua, refresh karo', 'error');
                }
              }}
              className="w-full py-3 rounded-xl bg-red-50 border-2 border-red-200 text-red-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-go-back-line text-base"></i>Customer Ne Lene Se Mana Kiya - Return Karo
            </button>

            {/* Per item size swap */}
            {activeOrder && activeOrder.items.map((item, idx) => {
              const swap = sizeSwaps.find(s => s.itemIndex === idx);
              if (!swap || !swap.orderedSize) return null; // skip items with no size

              // Get available sizes for this product from localStorage
              let availableSizes: string[] = [];
              try {
                const listed = JSON.parse(localStorage.getItem('listed_products') ?? '[]');
                const match = listed.find((p: { id: number; name?: string; price?: number; sizes?: string[] }) =>
                  p.id === item.id || (p.name === item.name && p.price === item.price)
                );
                if (match?.sizes && Array.isArray(match.sizes)) {
                  availableSizes = match.sizes;
                }
              } catch { /* ignore */ }

              // Fallback to standard sizes if no product found
              if (availableSizes.length === 0) {
                availableSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
              }

              const isSwapped = swap.deliveredSize !== swap.orderedSize;

              return (
                <div key={idx} className="bg-white rounded-2xl border border-gray-200 p-4">
                  {/* Product info */}
                  <div className="flex items-center gap-3 mb-4">
                    <img src={item.image} alt={item.name}
                      className="w-12 h-16 object-cover object-top rounded-xl shrink-0 bg-gray-100" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-bold text-sm truncate">{item.name}</p>
                      <p className="text-gray-500 text-xs">Qty: {item.quantity}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-semibold text-gray-500">Ordered:</span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">{swap.orderedSize}</span>
                        {isSwapped && (
                          <>
                            <i className="ri-arrow-right-line text-amber-500 text-xs"></i>
                            <span className="text-xs font-semibold text-amber-600">Delivering:</span>
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{swap.deliveredSize}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">
                    Kaun sa size deliver kar rahe ho?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map(sz => {
                      const isOrdered = sz === swap.orderedSize;
                      const isSelected = sz === swap.deliveredSize;
                      return (
                        <button
                          key={sz}
                          onClick={() => setSizeSwaps(prev => prev.map(s =>
                            s.itemIndex === idx ? { ...s, deliveredSize: sz } : s
                          ))}
                          className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                            isSelected && isOrdered
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : isSelected && !isOrdered
                              ? 'border-amber-500 bg-amber-500 text-white'
                              : isOrdered
                              ? 'border-gray-300 bg-gray-50 text-gray-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {sz}
                          {isOrdered && !isSelected && (
                            <span className="ml-1 text-[9px] text-gray-400">(ordered)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {isSwapped && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-swap-line text-amber-600 shrink-0 mt-0.5"></i>
                        <div>
                          <p className="text-amber-700 font-bold text-xs">Size Swap Confirm Hoga</p>
                          <p className="text-amber-600 text-xs mt-0.5">
                            {swap.orderedSize} size wapas stock mein aayega (+{item.quantity}) · {swap.deliveredSize} size minus hoga (-{item.quantity})
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Items without size (no swap needed) */}
            {activeOrder && activeOrder.items.some((item, idx) => {
              const swap = sizeSwaps.find(s => s.itemIndex === idx);
              return !swap || !swap.orderedSize;
            }) && (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">Items Without Size</p>
                <div className="space-y-2">
                  {activeOrder.items.map((item, idx) => {
                    const swap = sizeSwaps.find(s => s.itemIndex === idx);
                    if (swap && swap.orderedSize) return null;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <img src={item.image} alt={item.name} className="w-8 h-10 object-cover object-top rounded-lg" />
                        <div>
                          <p className="text-gray-700 text-xs font-medium">{item.name}</p>
                          <p className="text-gray-400 text-[10px]">No size — deliver as is</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              onClick={handleConfirmSizeSwap}
              className="w-full py-4 rounded-xl bg-gray-900 text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line text-xl"></i>
              Confirm &amp; Payment Karo
            </button>
          </div>
        )}

        {deliveryStep === 'payment' && (
          <div className="p-4 space-y-4 pb-8">
            {/* Payment step mein return button NAHI hona chahiye - payment lene ke baad delivery complete hoti hai */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 text-center">
              <p className="text-gray-500 text-sm mb-1">Collect Karna Hai</p>
              <p className="text-4xl font-bold text-gray-900">₹{activeOrder.final_amount}</p>
              <p className="text-gray-400 text-xs mt-1">{activeOrder.customer_name} se</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2"><i className="ri-bank-card-line text-gray-500"></i>Payment Method Select Karo</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button onClick={() => setSelectedPayment('cash')}
                  className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer text-left ${selectedPayment === 'cash' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                  {selectedPayment === 'cash' && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center"><i className="ri-check-line text-white text-xs"></i></div>}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${selectedPayment === 'cash' ? 'bg-amber-100' : 'bg-gray-200'}`}>
                    <i className={`ri-money-rupee-circle-fill text-xl ${selectedPayment === 'cash' ? 'text-amber-600' : 'text-gray-500'}`}></i>
                  </div>
                  <p className={`font-bold text-sm ${selectedPayment === 'cash' ? 'text-amber-700' : 'text-gray-700'}`}>Cash</p>
                  <p className="text-gray-400 text-xs mt-0.5">Customer se cash lo</p>
                </button>
                <button onClick={() => setSelectedPayment('online')}
                  className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer text-left ${selectedPayment === 'online' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                  {selectedPayment === 'online' && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"><i className="ri-check-line text-white text-xs"></i></div>}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${selectedPayment === 'online' ? 'bg-green-100' : 'bg-gray-200'}`}>
                    <i className={`ri-qr-code-line text-xl ${selectedPayment === 'online' ? 'text-green-600' : 'text-gray-500'}`}></i>
                  </div>
                  <p className={`font-bold text-sm ${selectedPayment === 'online' ? 'text-green-700' : 'text-gray-700'}`}>Online</p>
                  <p className="text-gray-400 text-xs mt-0.5">QR scan karke pay</p>
                </button>
              </div>
              {selectedPayment === 'online' && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4 text-center">
                  <p className="text-green-700 font-bold text-sm mb-3 flex items-center justify-center gap-2"><i className="ri-qr-code-line"></i>Company QR Code</p>
                  {companyQr ? (
                    <div className="w-48 h-48 mx-auto bg-white rounded-xl border-2 border-green-300 overflow-hidden flex items-center justify-center">
                      <img src={companyQr} alt="Company Payment QR" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-48 h-48 mx-auto bg-amber-50 rounded-xl border-2 border-amber-300 flex flex-col items-center justify-center p-4">
                      <i className="ri-error-warning-line text-amber-500 text-3xl mb-2"></i>
                      <p className="text-amber-700 text-xs font-medium">QR Code set nahi hai</p>
                    </div>
                  )}
                  <p className="text-green-600 text-xs mt-3 font-medium">Customer is QR ko scan karke ₹{activeOrder.final_amount} pay karega</p>
                </div>
              )}
              {selectedPayment === 'cash' && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><i className="ri-hand-coin-line text-amber-600"></i></div>
                    <div>
                      <p className="text-amber-700 font-bold text-sm">Cash Collection</p>
                      <p className="text-amber-600 text-xs mt-0.5">Customer se <strong>₹{activeOrder.final_amount}</strong> cash lo.</p>
                    </div>
                  </div>
                </div>
              )}
              {paymentError && <p className="text-red-500 text-xs mb-3 flex items-center gap-1"><i className="ri-error-warning-line"></i>{paymentError}</p>}
              <button onClick={handleConfirmPayment} disabled={!selectedPayment || paymentLoading}
                className={`w-full py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                  selectedPayment === 'cash' ? 'bg-amber-500 hover:bg-amber-600' : selectedPayment === 'online' ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400'
                }`}>
                {paymentLoading ? <><i className="ri-loader-4-line animate-spin"></i>Save ho raha hai...</> :
                  selectedPayment === 'cash' ? <><i className="ri-money-rupee-circle-line text-xl"></i>Cash Liya — Delivery Complete Karo</> :
                  selectedPayment === 'online' ? <><i className="ri-checkbox-circle-line text-xl"></i>Payment Hua — Delivery Complete Karo</> :
                  <>Payment Method Select Karo</>}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── PAYMENT RECORDS VIEW ────────────────────────────────────────────────────
  if (view === 'records') {
    return (
      <div className="min-h-screen bg-gray-50">
        {toast && (
          <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
            toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
          }`}>
            <i className={toast.type === 'success' ? 'ri-check-line' : toast.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
            {toast.msg}
          </div>
        )}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 h-14 flex items-center justify-between">
            <button onClick={() => setView('orders')} className="flex items-center gap-2 text-gray-600 text-sm font-medium cursor-pointer">
              <i className="ri-arrow-left-line text-lg"></i>Orders
            </button>
            <h2 className="text-gray-900 font-bold text-sm">Payment Records</h2>
            <button onClick={loadRecords} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all">
              <i className={`ri-refresh-line text-gray-600 ${recordsLoading ? 'animate-spin' : ''}`}></i>
            </button>
          </div>
        </header>
        <div className="p-4 space-y-4 pb-8">
          <div className="bg-white rounded-2xl border border-gray-200 p-1 flex gap-1">
            {(['today', 'month', 'all'] as const).map(p => (
              <button key={p} onClick={() => setRecordPeriod(p)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${recordPeriod === p ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
                {p === 'today' ? 'Today' : p === 'month' ? '30 Days' : 'All Time'}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><i className="ri-money-rupee-circle-fill text-amber-600"></i></div>
                <span className="text-amber-700 text-xs font-bold">Cash</span>
              </div>
              <p className="text-amber-900 font-bold text-xl">₹{cashTotal.toLocaleString('en-IN')}</p>
              <p className="text-amber-600 text-xs mt-0.5">{cashCount} orders</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center shrink-0"><i className="ri-qr-code-line text-green-600"></i></div>
                <span className="text-green-700 text-xs font-bold">Online</span>
              </div>
              <p className="text-green-900 font-bold text-xl">₹{onlineTotal.toLocaleString('en-IN')}</p>
              <p className="text-green-600 text-xs mt-0.5">{onlineCount} orders</p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><i className="ri-wallet-3-line text-gray-600"></i></div>
              <div>
                <p className="text-gray-500 text-xs">Total Collection</p>
                <p className="text-gray-900 font-bold text-lg">₹{(cashTotal + onlineTotal).toLocaleString('en-IN')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-xs">{records.length} deliveries</p>
            </div>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">Delivery History</p>
            {recordsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
                <p className="text-gray-400 text-sm">Load ho raha hai...</p>
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center"><i className="ri-file-list-3-line text-2xl text-gray-400"></i></div>
                <p className="text-gray-500 text-sm">Koi record nahi mila</p>
              </div>
            ) : (
              <div className="space-y-2">
                {records.map(record => (
                  <div key={record.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${record.collected_payment_method === 'cash' ? 'bg-amber-100' : 'bg-green-100'}`}>
                      <i className={`text-lg ${record.collected_payment_method === 'cash' ? 'ri-money-rupee-circle-fill text-amber-600' : 'ri-qr-code-line text-green-600'}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-semibold text-sm truncate">{record.customer_name}</p>
                      <p className="text-gray-400 text-xs">{record.customer_phone}</p>
                      <p className="text-gray-400 text-xs">{new Date(record.payment_collected_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-gray-900 font-bold text-base">₹{record.final_amount}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${record.collected_payment_method === 'cash' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {record.collected_payment_method === 'cash' ? 'Cash' : 'Online'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── RETURN MODAL ───────────────────────────────────────────────────────────
  const ReturnModal = () => {
    if (!showReturnModal || !returnOrderForModal) return null;

    // Quick-select all items for "customer not accepting entire order"
    const allSelected = returnItems.length > 0 && returnItems.every(r => r.selected);
    const selectAllWithReason = (reason: string) => {
      setReturnItems(prev => prev.map(r => ({ ...r, selected: true, reason })));
    };

    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white w-full rounded-t-3xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-500 text-lg"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Order Reject / Return</p>
                <p className="text-gray-400 text-xs">Order #{returnOrderForModal.id.toUpperCase().slice(0, 8)} · ₹{returnOrderForModal.final_amount}</p>
              </div>
            </div>
            <button onClick={() => setShowReturnModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="overflow-y-auto flex-1 p-5 space-y-4">
            {/* Warning banner */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-3.5 flex items-start gap-2.5">
              <i className="ri-error-warning-line text-red-500 shrink-0 mt-0.5 text-base"></i>
              <div>
                <p className="text-red-700 font-bold text-xs">Order Cancel Ho Jaayega</p>
                <p className="text-red-600 text-xs mt-0.5 leading-relaxed">
                  Yeh order database mein <strong>Cancelled</strong> ho jaayega. Customer ko notification milegi. Selected items ka stock wapas aa jaayega.
                </p>
              </div>
            </div>

            {/* Quick action — customer not accepting entire order */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <p className="text-gray-700 font-bold text-xs mb-2.5 flex items-center gap-1.5">
                <i className="ri-flashlight-line text-amber-500"></i>Quick Reject (Poora Order)
              </p>
              <div className="flex flex-wrap gap-2">
                {['Customer ne lene se mana kar diya', 'Customer ghar pe nahi tha', 'Customer ne cancel kar diya'].map(reason => (
                  <button
                    key={reason}
                    onClick={() => selectAllWithReason(reason)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                      allSelected && returnItems[0]?.reason === reason
                        ? 'border-red-500 bg-red-500 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-red-300 hover:text-red-600'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            {/* Individual item selection */}
            <div>
              <p className="text-gray-700 font-bold text-sm mb-3">Ya Items Select Karo:</p>
              <div className="space-y-3">
                {returnOrderForModal.items.map((item, idx) => {
                  const ri = returnItems.find(r => r.itemIndex === idx);
                  if (!ri) return null;
                  // Check if a size-swap happened for this item
                  const swapEntry = sizeSwaps.find(s => s.itemIndex === idx);
                  const wasSwapped = swapEntry && swapEntry.deliveredSize !== swapEntry.orderedSize && swapEntry.deliveredSize;

                  return (
                    <div key={idx} className={`rounded-2xl border-2 p-4 transition-all ${ri.selected ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <button
                          onClick={() => setReturnItems(prev => prev.map(r => r.itemIndex === idx ? { ...r, selected: !r.selected, reason: !r.selected ? r.reason : '' } : r))}
                          className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 cursor-pointer transition-all ${ri.selected ? 'bg-red-500 border-red-500' : 'border-gray-300'}`}
                        >
                          {ri.selected && <i className="ri-check-line text-white text-xs"></i>}
                        </button>
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-10 h-12 object-cover object-top rounded-lg shrink-0" />
                        ) : (
                          <div className="w-10 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0"><i className="ri-t-shirt-line text-gray-400"></i></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-semibold text-sm truncate">{item.name}</p>
                          <p className="text-gray-400 text-xs">
                            Qty: {item.quantity}
                            {wasSwapped ? (
                              <span className="ml-1 text-amber-600 font-semibold">· Delivered: {swapEntry?.deliveredSize} (was {swapEntry?.orderedSize})</span>
                            ) : item.size ? (
                              ` · Size: ${item.size}`
                            ) : null}
                          </p>
                          {wasSwapped && (
                            <p className="text-[10px] text-amber-500 mt-0.5">
                              <i className="ri-information-line mr-0.5"></i>
                              {swapEntry?.deliveredSize} size ka stock wapas aayega
                            </p>
                          )}
                        </div>
                      </div>
                      {ri.selected && (
                        <div className="mt-2">
                          <p className="text-gray-600 text-xs font-semibold mb-2">Return Reason:</p>
                          <div className="flex flex-wrap gap-2">
                            {RETURN_REASONS.map(reason => (
                              <button
                                key={reason}
                                onClick={() => setReturnItems(prev => prev.map(r => r.itemIndex === idx ? { ...r, reason } : r))}
                                className={`px-2.5 py-1.5 rounded-xl text-xs font-medium border transition-all cursor-pointer ${ri.reason === reason ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'}`}
                              >
                                {reason}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="p-5 border-t border-gray-100 shrink-0 space-y-2">
            {returnItems.some(r => r.selected) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
                <i className="ri-stack-line text-amber-600 text-sm shrink-0"></i>
                <p className="text-amber-700 text-xs font-medium">
                  {returnItems.filter(r => r.selected).length} item(s) selected — stock wapas aayega
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowReturnModal(false)} className="flex-1 py-3.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-all">
                Wapas Jao
              </button>
              <button
                onClick={handleSubmitReturn}
                disabled={returnSubmitting || !returnItems.some(r => r.selected) || returnItems.filter(r => r.selected).some(r => !r.reason)}
                className="flex-1 py-3.5 rounded-xl bg-red-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {returnSubmitting
                  ? <><i className="ri-loader-4-line animate-spin"></i>Ho raha hai...</>
                  : <><i className="ri-close-circle-line"></i>Order Reject Karo</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── ORDERS LIST VIEW ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
          toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : toast.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          {toast.msg}
        </div>
      )}
      <ReturnModal />

      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gray-900 flex items-center justify-center">
              <i className="ri-e-bike-2-fill text-white text-sm"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-sm leading-none">{dbName}</p>
              <p className="text-gray-400 text-xs">{dbPhone}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setView('records')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold cursor-pointer hover:bg-gray-200 transition-all whitespace-nowrap">
              <i className="ri-bar-chart-2-line"></i>Records
            </button>
            <button onClick={loadOrders} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all">
              <i className={`ri-refresh-line text-gray-600 ${loading ? 'animate-spin' : ''}`}></i>
            </button>
            <button onClick={handleLogout} className="px-3 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold cursor-pointer hover:bg-gray-200 transition-all whitespace-nowrap">
              <i className="ri-logout-box-line mr-1"></i>Logout
            </button>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-3 pb-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-bold text-gray-900">Pending Deliveries</h2>
          <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-time-line"></i>Auto refresh: 15s</span>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
            <p className="text-gray-400 text-sm">Orders load ho rahe hain...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-3xl text-green-400"></i>
            </div>
            <div className="text-center">
              <p className="text-gray-700 font-semibold">Koi pending delivery nahi!</p>
              <p className="text-gray-400 text-sm mt-1">Jab admin order confirm karega, yahan dikhega</p>
            </div>
          </div>
        ) : (
          orders.map(order => (
            <div key={order.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className={`px-4 py-2 flex items-center justify-between ${order.status === 'shipped' ? 'bg-orange-50 border-b border-orange-100' : 'bg-amber-50 border-b border-amber-100'}`}>
                <span className={`text-xs font-bold flex items-center gap-1.5 ${order.status === 'shipped' ? 'text-orange-600' : 'text-amber-600'}`}>
                  <span className={`w-2 h-2 rounded-full ${order.status === 'shipped' ? 'bg-orange-500 animate-pulse' : 'bg-amber-500'}`}></span>
                  {order.status === 'shipped' ? 'Out of Delivery' : 'Ready to Pick'}
                </span>
                <span className="text-xs text-gray-400">{new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><i className="ri-user-3-line text-gray-500"></i></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-bold text-sm">{order.customer_name}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-1"><i className="ri-phone-line"></i>{order.customer_phone}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5"><i className="ri-map-pin-line"></i><span className="truncate">{order.customer_address}, {order.customer_city}</span></p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-gray-900 font-bold text-base">₹{order.final_amount}</p>
                    <p className="text-gray-400 text-xs">Total</p>
                  </div>
                </div>
                <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="shrink-0 flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5 border border-gray-100">
                      <img src={item.image} alt={item.name} className="w-6 h-8 object-cover object-top rounded" />
                      <div>
                        <p className="text-gray-700 text-xs font-medium whitespace-nowrap max-w-[80px] truncate">{item.name}</p>
                        {item.size && <p className="text-gray-400 text-[10px]">×{item.quantity} · {item.size}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                {order.status === 'confirmed' ? (
                  <button onClick={() => handlePickupOrder(order)}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-e-bike-2-fill text-base"></i>Order Pick Karo
                  </button>
                ) : (
                  <button onClick={() => {
                    setActiveOrder(order); setDeliveryStep('map-otp'); setOtpInput(''); setOtpError('');
                    setOtpVerified(false); setSelectedPayment(null); setView('active-delivery');
                    // Always restart location tracking when continuing delivery
                    setTimeout(() => startLocationTracking(order.id), 100);
                  }} className="w-full py-3 rounded-xl bg-orange-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-map-pin-2-fill text-base"></i>Delivery Continue
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
